import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const LOGO_URL = "/brand/colacicco-logo-nero.png";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: email+phone, 2: create password or full form
  const [userExists, setUserExists] = useState(false);
  const [existingUserData, setExistingUserData] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    name: "",
    surname: "",
    address: "",
    password: "",
    confirmPassword: ""
  });

  const checkUser = async () => {
    if (!formData.email || !formData.phone) {
      toast.error("Inserisci sia email che numero di telefono");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/check-user`, {
        email: formData.email,
        phone: formData.phone
      });
      
      if (response.data.exists) {
        if (response.data.has_password) {
          toast.info("Account già registrato. Vai al login.");
          setTimeout(() => navigate("/login"), 1500);
          return;
        }
        // User exists in Bitrix24 but no password
        setUserExists(true);
        setExistingUserData({
          name: response.data.name,
          surname: response.data.surname,
          bitrix24_id: response.data.bitrix24_id
        });
        toast.success("Ti abbiamo trovato! Crea una password per accedere.");
      } else {
        // User doesn't exist
        setUserExists(false);
        setExistingUserData(null);
      }
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la verifica");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      await checkUser();
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }

    // If new user, validate required fields
    if (!userExists) {
      if (!formData.name || !formData.surname) {
        toast.error("Nome e cognome sono obbligatori");
        return;
      }
    }

    setLoading(true);
    try {
      const registrationData = {
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        ...(userExists ? {} : {
          name: formData.name,
          surname: formData.surname,
          address: formData.address
        })
      };
      
      await register(registrationData);
      toast.success("Registrazione completata!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="mb-10">
        <img src={LOGO_URL} alt="Colacicco Legno" className="h-14 w-auto" />
      </div>

      {/* Form */}
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-light text-white tracking-wide">
            {step === 1 ? "Accedi o Registrati" : userExists ? "Bentornato!" : "Crea Account"}
          </h1>
          <p className="text-sm text-white/60">
            {step === 1 
              ? "Inserisci i tuoi dati per continuare" 
              : userExists 
                ? `Ciao ${existingUserData?.name || ""}! Crea una password per accedere`
                : "Completa la registrazione"
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {step === 1 ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70 text-sm">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="tuaemail@esempio.com"
                  className="bg-zinc-900 border-zinc-800 text-white h-12 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/70 text-sm">Numero di telefono *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+39 123 456 7890"
                  className="bg-zinc-900 border-zinc-800 text-white h-12 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  required
                  data-testid="register-phone-input"
                />
              </div>
            </>
          ) : (
            <>
              {/* Show existing user info if found in Bitrix24 */}
              {userExists && existingUserData && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                  <p className="text-emerald-400 text-sm">
                    Account trovato per: <strong>{existingUserData.name} {existingUserData.surname}</strong>
                  </p>
                </div>
              )}

              {/* Full form for new users */}
              {!userExists && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white/70 text-sm">Nome *</Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Mario"
                        className="bg-zinc-900 border-zinc-800 text-white h-12 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="surname" className="text-white/70 text-sm">Cognome *</Label>
                      <Input
                        id="surname"
                        type="text"
                        value={formData.surname}
                        onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                        placeholder="Rossi"
                        className="bg-zinc-900 border-zinc-800 text-white h-12 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        required
                        data-testid="register-surname-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-white/70 text-sm">Indirizzo</Label>
                    <Input
                      id="address"
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Via Roma 1, 70100 Bari"
                      className="bg-zinc-900 border-zinc-800 text-white h-12 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                      data-testid="register-address-input"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/70 text-sm">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Minimo 6 caratteri"
                  className="bg-zinc-900 border-zinc-800 text-white h-12 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  required
                  data-testid="register-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white/70 text-sm">Conferma Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Ripeti la password"
                  className="bg-zinc-900 border-zinc-800 text-white h-12 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-full transition-all"
            data-testid="register-submit-btn"
          >
            {loading 
              ? "Caricamento..." 
              : step === 1 
                ? "Continua" 
                : userExists
                  ? "Crea Password e Accedi"
                  : "Registrati"
            }
          </Button>

          {step === 2 && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setStep(1);
                setUserExists(false);
                setExistingUserData(null);
              }}
              className="w-full text-white/60 hover:text-white"
            >
              ← Indietro
            </Button>
          )}
        </form>

        {step === 1 && (
          <div className="text-center">
            <p className="text-sm text-white/60">
              Hai già un account con password?{" "}
              <Link to="/login" className="text-emerald-500 hover:text-emerald-400 font-medium">
                Accedi
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
